import { useEffect, useRef } from 'react'
import { Canvas, Text, Control, FabricImage } from 'fabric'
import { updateTextObject, removeTextObject } from '../utils/textObjectUtils'
import {
    setupTextObjectControls,
    setupTextObjectEventHandlers,
    applyTextBorderStyles,
    waitForFontLoad,
    getTextObjects,
} from '../../utils/text/textObject'
import { createDefaultResizeControl } from '../../utils/text/resizeControl'
import { calculateTextPosition } from '../../utils/text/textPosition'
import { clampFontSize } from '../../utils/validation'
import { convertHexWithAlphaToRgba } from '../../utils/color/colorConversion'
import {
    TEXT_BORDER_STYLES,
    TEXT_OBJECT_PADDING,
    TEXT_OBJECT_BACKGROUND_BORDER_RADIUS,
} from '../../constants'
import { applyOptimalRenderingSettings } from '../utils/canvasUtils'
import type {
    MarkingPosition,
    TextAlignment,
    FontFamily,
    FabricObjectModifiedEvent,
} from '../../types'
import type { MediaDimensions } from '../types'
import { useTextObjectCleanup } from './useTextObjectCleanup'
import { useTextObjectFontSizeUpdate } from './useTextObjectFontSizeUpdate'
import { createTextObject } from './useTextObjectCreation'

/**
 * Параметры для хука управления текстовым объектом
 */
export interface UseTextObjectManagementParams {
    text: string | null
    fontSize: number
    textColor: string
    textAlignment: TextAlignment
    fontFamily: FontFamily
    position: MarkingPosition | null
    hasBackground: boolean
    backgroundColor: string | null
    fabricCanvasRef: React.MutableRefObject<Canvas | null>
    textObjectRef: React.MutableRefObject<Text | null>
    positionRef: React.MutableRefObject<MarkingPosition | null>
    previousAlignmentRef: React.MutableRefObject<TextAlignment>
    isTextJustCreatedRef: React.MutableRefObject<boolean>
    isCreatingInRAFRef: React.MutableRefObject<boolean>
    isScalingFromSliderRef: React.MutableRefObject<boolean>
    safeArea: {
        top: number
        bottom: number
        left: number
        right: number
    }
    onFontSizeChange?: (size: number) => void
    onPositionChange?: (position: MarkingPosition) => void
    handleTextScaling: () => void
    handleTextModified: (e: FabricObjectModifiedEvent) => void
    canvasReadyTrigger?: number // Триггер для перезапуска хука при создании canvas
    isMediaLoading?: boolean // Флаг загрузки медиа - текст не должен создаваться до завершения загрузки
    imageObjectRef: React.MutableRefObject<FabricImage | null> // Ref на изображение для проверки готовности
    mediaDimensions: MediaDimensions | null // Размеры медиа для проверки готовности
}

/**
 * Параметры для обновления существующего текстового объекта
 * @internal Используется только внутри useTextObjectManagement
 */
interface UpdateExistingTextObjectParams {
    textObj: Text
    fabricCanvas: Canvas
    text: string
    fontSize: number
    textColor: string
    textAlignment: TextAlignment
    fontFamily: FontFamily
    hasBackground: boolean
    backgroundColor: string | null
    position: MarkingPosition | null | undefined
    positionRef: React.MutableRefObject<MarkingPosition | null>
    previousAlignmentRef: React.MutableRefObject<TextAlignment>
    isTextJustCreatedRef: React.MutableRefObject<boolean>
    isScalingFromSliderRef: React.MutableRefObject<boolean>
    fabricCanvasRef: React.MutableRefObject<Canvas | null>
    handleTextScaling: () => void
    handleTextModified: (e: FabricObjectModifiedEvent) => void
    onPositionChange?: (position: MarkingPosition) => void
    safeArea: {
        top: number
        bottom: number
        left: number
        right: number
    }
}

/**
 * Расчет максимальной ширины текста с учетом padding
 * @param canvasWidth - ширина canvas
 * @param hasBackground - есть ли подложка
 * @param backgroundColor - цвет подложки
 * @returns максимальная ширина текста
 * @internal Используется только внутри useTextObjectManagement
 */
function calculateTextMaxWidth(
    canvasWidth: number,
    hasBackground: boolean,
    backgroundColor: string | null
): number {
    const borderPadding = TEXT_OBJECT_PADDING.BORDER * 2
    const backgroundPadding =
        hasBackground && backgroundColor !== null
            ? TEXT_OBJECT_PADDING.BACKGROUND * 2
            : 0
    return Math.max(
        TEXT_OBJECT_PADDING.MIN_WIDTH,
        canvasWidth - borderPadding - backgroundPadding
    )
}

/**
 * Обновление свойств текстового объекта
 * @param textObj - текстовый объект Fabric.js
 * @param text - текст
 * @param fontSize - размер шрифта
 * @param textColor - цвет текста
 * @param textAlignment - выравнивание текста
 * @param fontFamily - семейство шрифта
 * @param hasBackground - есть ли подложка
 * @param backgroundColor - цвет подложки
 * @param maxWidth - максимальная ширина текста
 * @internal Используется только внутри useTextObjectManagement
 */
function updateTextObjectProperties(
    textObj: Text,
    text: string,
    fontSize: number,
    textColor: string,
    textAlignment: TextAlignment,
    fontFamily: FontFamily,
    hasBackground: boolean,
    backgroundColor: string | null,
    maxWidth: number
): void {
    updateTextObject({
        textObj,
        updates: {
            text,
            fontSize: clampFontSize(fontSize),
            fill: textColor,
            textAlign: textAlignment,
            fontFamily,
            textBackgroundColor:
                hasBackground && backgroundColor ? backgroundColor : null,
        },
    })

    textObj.set({ width: maxWidth })
}

/**
 * Пересчет размеров текстового объекта
 * @param textObj - текстовый объект Fabric.js
 * @internal Используется только внутри useTextObjectManagement
 */
function recalculateTextDimensions(textObj: Text): void {
    textObj.initDimensions()
}

/**
 * Обработка специфичной логики для \"проблемных\" шрифтов (Inter, Montserrat)
 * @param textObj - текстовый объект Fabric.js
 * @param fabricCanvas - canvas Fabric.js
 * @param fontFamily - семейство шрифта
 * @param maxWidth - максимальная ширина текста
 * @param hasBackground - есть ли подложка
 * @param backgroundColor - цвет подложки
 * @param textColor - цвет текста с прозрачностью
 * @internal Используется только внутри useTextObjectManagement
 */
function handleFontSpecificLogic(
    textObj: Text,
    fabricCanvas: Canvas,
    fontFamily: FontFamily,
    hasBackground: boolean,
    backgroundColor: string | null,
    textColor: string
): void {
    const isKerningSensitiveFont =
        fontFamily === 'Inter' || fontFamily === 'Montserrat'

    if (isKerningSensitiveFont) {
        // Для Inter пересчитываем размеры после применения шрифта
        requestAnimationFrame(() => {
            if (textObj && fabricCanvas.getObjects().includes(textObj)) {
                // Принудительно применяем charSpacing для проблемных шрифтов
                // Это важно, так как charSpacing может быть сброшен при обновлении объекта
                textObj.set({ charSpacing: -15 })
                
                textObj.initDimensions()

                // Применяем подложку после initDimensions()
                applyTextObjectBackground(
                    textObj,
                    hasBackground,
                    backgroundColor,
                    fabricCanvas
                )

                // Принудительно применяем цвет с прозрачностью
                const rgbaColor = convertHexWithAlphaToRgba(textColor)
                textObj.set({ fill: rgbaColor })

                textObj.setCoords()
                fabricCanvas.renderAll()
            }
        })
    }
}

/**
 * Применение подложки к текстовому объекту
 * @param textObj - текстовый объект Fabric.js
 * @param hasBackground - есть ли подложка
 * @param backgroundColor - цвет подложки
 * @param fabricCanvas - canvas для перерисовки (опционально)
 * @internal Используется только внутри useTextObjectManagement
 */
function applyTextObjectBackground(
    textObj: Text,
    hasBackground: boolean,
    backgroundColor: string | null,
    fabricCanvas?: Canvas | null
): void {
    if (hasBackground && backgroundColor !== null) {
        const rgbaBackgroundColor = convertHexWithAlphaToRgba(backgroundColor)
        const padding = TEXT_OBJECT_PADDING.BACKGROUND
        const borderRadius = TEXT_OBJECT_BACKGROUND_BORDER_RADIUS
        
        // Применяем подложку с padding и border radius
        // CustomText переопределяет _renderBackground для визуального отображения этих свойств
        textObj.set({
            textBackgroundColor: rgbaBackgroundColor,
            textBackgroundPadding: padding,
            textBackgroundBorderRadius: borderRadius,
        })
        
        // Пересчитываем размеры после применения подложки
        textObj.initDimensions()
        textObj.setCoords()
        
        // ПОВТОРНО применяем подложку после initDimensions(), так как initDimensions() может сбросить значения
        // Это критично для правильного отображения подложки
        textObj.set({
            textBackgroundColor: rgbaBackgroundColor,
            textBackgroundPadding: padding,
            textBackgroundBorderRadius: borderRadius,
        })
        
        // Перерисовываем canvas, если он передан
        if (fabricCanvas) {
            fabricCanvas.renderAll()
        }
    } else {
        textObj.set({
            textBackgroundColor: undefined,
            textBackgroundPadding: undefined,
            textBackgroundBorderRadius: undefined,
        })
    }
}

/**
 * Обновление позиции текстового объекта на canvas
 * Рассчитывает позицию в пикселях на основе позиции в процентах и размеров текста
 * @param textObj - текстовый объект Fabric.js
 * @param fabricCanvas - canvas Fabric.js
 * @param position - позиция в процентах (0-100)
 * @param textAlignment - выравнивание текста
 * @param textWidth - ширина текста в пикселях
 * @param textHeight - высота текста в пикселях
 * @internal Используется только внутри useTextObjectManagement
 */
function updateTextObjectPosition(
    textObj: Text,
    fabricCanvas: Canvas,
    position: MarkingPosition,
    textAlignment: TextAlignment,
    textWidth: number,
    textHeight: number,
    safeArea: {
        top: number
        bottom: number
        left: number
        right: number
    }
): void {
    const canvasWidth = fabricCanvas.width
    const canvasHeight = fabricCanvas.height

    const textPosition = calculateTextPosition({
        position,
        canvasWidth,
        canvasHeight,
        textAlign: textAlignment,
        textWidth,
        textHeight,
    })

    // Округляем координаты до целых пикселей для четкости текста
    // calculateTextPosition уже округляет, но делаем это еще раз для гарантии
    textObj.set({
        left: Math.round(textPosition.left),
        top: Math.round(textPosition.top),
    })
}

/**
 * Обновление позиции текстового объекта, если это необходимо
 * @param textObj - текстовый объект Fabric.js
 * @param fabricCanvas - canvas Fabric.js
 * @param position - позиция в процентах (может быть undefined)
 * @param positionRef - ref для хранения позиции
 * @param textAlignment - выравнивание текста
 * @param alignmentChanged - изменилось ли выравнивание
 * @param isTextJustCreatedRef - ref для флага создания текста
 * @param safeArea - безопасная область
 * @internal Используется только внутри useTextObjectManagement
 */
function updateTextObjectPositionIfNeeded(
    textObj: Text,
    fabricCanvas: Canvas,
    position: MarkingPosition | null | undefined,
    positionRef: React.MutableRefObject<MarkingPosition | null>,
    textAlignment: TextAlignment,
    alignmentChanged: boolean,
    isTextJustCreatedRef: React.MutableRefObject<boolean>,
    safeArea: {
        top: number
        bottom: number
        left: number
        right: number
    }
): void {
    if (position !== undefined) {
        const previousPosition = positionRef.current
        positionRef.current = position ?? null

        const hasPositionChanged =
            position !== null &&
            previousPosition !== null &&
            (position.x !== previousPosition.x ||
                position.y !== previousPosition.y)
        const shouldUpdatePosition =
            alignmentChanged ||
            (!isTextJustCreatedRef.current && hasPositionChanged)

        if (shouldUpdatePosition && position !== null) {
            const textWidth = textObj.width * (textObj.scaleX ?? 1)
            const textHeight = textObj.height * (textObj.scaleY ?? 1)
            updateTextObjectPosition(
                textObj,
                fabricCanvas,
                position,
                textAlignment,
                textWidth,
                textHeight,
                safeArea
            )
        }
    } else if (alignmentChanged && positionRef.current !== null) {
        const textWidth = textObj.width * (textObj.scaleX ?? 1)
        const textHeight = textObj.height * (textObj.scaleY ?? 1)
        updateTextObjectPosition(
            textObj,
            fabricCanvas,
            positionRef.current,
            textAlignment,
            textWidth,
            textHeight,
            safeArea
        )
    }
}

/**
 * Настройка контролов текстового объекта, если это необходимо
 * @param textObj - текстовый объект Fabric.js
 * @param fabricCanvas - canvas Fabric.js
 * @param fabricCanvasRef - ref для canvas
 * @param handleTextScaling - обработчик изменения размера
 * @param isScalingFromSliderRef - ref для флага изменения размера через слайдер
 * @internal Используется только внутри useTextObjectManagement
 */
function setupTextObjectControlsIfNeeded(
    textObj: Text,
    fabricCanvas: Canvas,
    fabricCanvasRef: React.MutableRefObject<Canvas | null>,
    handleTextScaling: () => void,
    isScalingFromSliderRef: React.MutableRefObject<boolean>
): void {
    if (textObj.hasControls !== true) {
        textObj.set({
            hasControls: true,
            hasBorders: true,
            borderColor: TEXT_BORDER_STYLES.COLOR,
            borderDashArray: [...TEXT_BORDER_STYLES.DASH_ARRAY],
            borderScaleFactor: TEXT_BORDER_STYLES.SCALE_FACTOR,
        })

        const isResizeControlValid = (): boolean => {
            return (
                textObj.controls.tr !== undefined &&
                textObj.controls.tr instanceof Control
            )
        }

        if (!isResizeControlValid()) {
            const updateResizeControl = createDefaultResizeControl({
                canvas: fabricCanvasRef.current,
                onScaling: handleTextScaling,
                isScalingFromSlider: () => isScalingFromSliderRef.current,
            })
            setupTextObjectControls(textObj, updateResizeControl)
        }
    }
}

/**
 * Обновление существующего текстового объекта на canvas
 * Обновляет свойства текста, позицию, контролы и обработчики событий
 * @param params - параметры для обновления текстового объекта
 * @internal Используется только внутри useTextObjectManagement
 */
async function updateExistingTextObject(
    params: UpdateExistingTextObjectParams
): Promise<void> {
    const {
        textObj,
        fabricCanvas,
        text,
        fontSize,
        textColor,
        textAlignment,
        fontFamily,
        hasBackground,
        backgroundColor,
        position,
        positionRef,
        previousAlignmentRef,
        isTextJustCreatedRef,
        isScalingFromSliderRef,
        fabricCanvasRef,
        handleTextScaling,
        handleTextModified,
        onPositionChange,
        safeArea,
    } = params

    // Временно отключаем обработчики
    textObj.off('scaling', handleTextScaling)
    if (onPositionChange) {
        fabricCanvas.off('object:modified', handleTextModified)
    }

    // Проверяем, изменилось ли выравнивание
    const alignmentChanged = previousAlignmentRef.current !== textAlignment
    if (alignmentChanged) {
        previousAlignmentRef.current = textAlignment
    }

    // Рассчитываем максимальную ширину текста
    const canvasWidth = fabricCanvas.width
    const maxWidth = calculateTextMaxWidth(
        canvasWidth,
        hasBackground,
        backgroundColor
    )

    // Ждем загрузки шрифта перед обновлением
    await waitForFontLoad(fontFamily)

    // Обновляем свойства текста
    updateTextObjectProperties(
        textObj,
            text,
        fontSize,
        textColor,
        textAlignment,
            fontFamily,
        hasBackground,
        backgroundColor,
        maxWidth
    )

    // Пересчитываем размеры текста
    recalculateTextDimensions(textObj)

    // Обновляем настройки рендеринга canvas для оптимальной четкости текста
    // Для маленьких шрифтов используем более резкие настройки
    const canvasElement = fabricCanvas.getElement()
    if (canvasElement) {
        const ctx = canvasElement.getContext('2d')
        if (ctx) {
            applyOptimalRenderingSettings(ctx, fontSize)
        }
    }

    // Применяем подложку
    applyTextObjectBackground(textObj, hasBackground, backgroundColor, fabricCanvas)

    // Обрабатываем специфичную логику для шрифта Inter
    // Важно: вызываем после применения подложки, чтобы она была применена и в requestAnimationFrame
    handleFontSpecificLogic(
        textObj,
        fabricCanvas,
        fontFamily,
        hasBackground,
        backgroundColor,
        textColor
    )

    // Обновляем originX в зависимости от выравнивания
    textObj.set({
        originX: textAlignment,
    })
    
    // ВАЖНО: При изменении выравнивания нужно обновить координаты и перерисовать canvas,
    // чтобы убрать старые контуры и отобразить новые
    if (alignmentChanged) {
        textObj.setCoords()
        fabricCanvas.renderAll()
    }

    // Обновляем позицию если нужно
    updateTextObjectPositionIfNeeded(
                textObj,
                fabricCanvas,
                position,
        positionRef,
                textAlignment,
        alignmentChanged,
        isTextJustCreatedRef,
            safeArea
        )

    // Сбрасываем флаг после первого обновления
    if (isTextJustCreatedRef.current) {
        isTextJustCreatedRef.current = false
    }

    // Настраиваем контролы если нужно
    setupTextObjectControlsIfNeeded(
        textObj,
        fabricCanvas,
        fabricCanvasRef,
        handleTextScaling,
        isScalingFromSliderRef
    )

    // Принудительно применяем настройки обводки
    applyTextBorderStyles(textObj)

    // Настраиваем обработчики событий
    setupTextObjectEventHandlers({
        textObj,
        fabricCanvas,
        onScaling: handleTextScaling,
        onPositionChange: onPositionChange ? handleTextModified : undefined,
    })

    // Принудительно применяем цвет с прозрачностью после всех операций
    // Это гарантирует, что прозрачность будет применена при загрузке
    const finalRgbaColor = convertHexWithAlphaToRgba(textColor)
    textObj.set({
        fill: finalRgbaColor,
    })

    textObj.setCoords()
    fabricCanvas.renderAll()
}

/**
 * Хук для управления текстовым объектом на canvas
 * Создает, обновляет и удаляет текстовый объект
 */
export function useTextObjectManagement(
    params: UseTextObjectManagementParams
): void {
    const {
        text,
        fontSize,
        textColor,
        textAlignment,
        fontFamily,
        position,
        hasBackground,
        backgroundColor,
        fabricCanvasRef,
        textObjectRef,
        positionRef,
        previousAlignmentRef,
        isTextJustCreatedRef,
        isCreatingInRAFRef,
        isScalingFromSliderRef,
        safeArea,
        onFontSizeChange,
        onPositionChange,
        handleTextScaling,
        handleTextModified,
        canvasReadyTrigger,
        isMediaLoading,
        imageObjectRef,
        mediaDimensions,
    } = params

    // Ref для отслеживания выполнения обновления объекта
    // Это предотвращает создание дубликатов при асинхронных операциях
    const isUpdatingRef = useRef(false)

    // Синхронизация positionRef с пропсом position
    useEffect(() => {
        positionRef.current = position ?? null
    }, [position, positionRef])

    // Очистка текстовых объектов при изменении canvasReadyTrigger
    useTextObjectCleanup({
        fabricCanvasRef,
        textObjectRef,
        canvasReadyTrigger,
        isUpdatingRef,
    })

    // Создание и обновление текстового объекта
    useEffect(() => {
        // Ждем, пока canvas будет инициализирован
        if (!fabricCanvasRef.current) {
            return
        }

        // Улучшенная проверка готовности медиа:
        // 1. Медиа не должно загружаться
        // 2. Изображение должно быть добавлено на canvas
        // 3. Размеры медиа должны быть известны
        // Это предотвращает неправильное позиционирование при первом открытии
        if (
            isMediaLoading ||
            !imageObjectRef.current ||
            !mediaDimensions
        ) {
            return
        }

        const fabricCanvas = fabricCanvasRef.current

        // Проверяем, что canvas привязан к DOM
        const canvasElement = fabricCanvas.getElement()
        if (!canvasElement || !canvasElement.parentElement) {
            return
        }

        // Если текст пустой или не передан, удаляем текстовый объект
        if (!text || text.trim() === '') {
            if (textObjectRef.current) {
                removeTextObject(fabricCanvas, textObjectRef.current)
                textObjectRef.current = null
            }
            isUpdatingRef.current = false
            return
        }

        // Асинхронная функция для создания/обновления текстового объекта
        const createOrUpdateTextObject = async () => {
            // Проверяем, не выполняется ли уже обновление
            // Это предотвращает создание дубликатов при быстрых изменениях (например, при изменении шрифта)
            if (isUpdatingRef.current) {
                return
            }

            // Проверяем, есть ли текстовые объекты на canvas
            // Ищем все текстовые объекты, а не только тот, что в ref
            const allObjects = fabricCanvas.getObjects()
            const textObjects = allObjects.filter(
                (obj) => obj instanceof Text
            ) as Text[]

            // Определяем существующий объект:
            // 1. Если объект в ref существует на canvas - используем его
            // 2. Иначе, если есть любой текстовый объект на canvas - используем первый
            const existingTextObject =
                textObjectRef.current &&
                textObjects.includes(textObjectRef.current)
                    ? textObjectRef.current
                    : textObjects.length > 0
                      ? textObjects[0]
                      : null

            // Если текстовый объект уже существует на canvas, обновляем его свойства
            // Важно: проверяем существование объекта на canvas, а не только совпадение с ref
            // Это нужно для случаев, когда ref еще не обновлен (например, при изменении шрифта)
            const isTextObjectExists =
                existingTextObject && !isCreatingInRAFRef.current

            if (isTextObjectExists && existingTextObject) {
                // Устанавливаем флаг обновления
                isUpdatingRef.current = true

                // Обновляем ref, если он не совпадает с найденным объектом
                // Это важно для случаев, когда ref был сброшен, но объект остался на canvas
                if (textObjectRef.current !== existingTextObject) {
                    textObjectRef.current = existingTextObject
                }

                try {
                await updateExistingTextObject({
                        textObj: existingTextObject,
                    fabricCanvas,
                    text,
                    fontSize,
                    textColor,
                    textAlignment,
                    fontFamily,
                    hasBackground,
                    backgroundColor,
                    position,
                    positionRef,
                    previousAlignmentRef,
                    isTextJustCreatedRef,
                    isScalingFromSliderRef,
                    fabricCanvasRef,
                    handleTextScaling,
                    handleTextModified,
                    onPositionChange,
                    safeArea,
                })
                } finally {
                    // Сбрасываем флаг обновления после завершения
                    isUpdatingRef.current = false
                }
                return
            }

            // Проверяем еще раз, не выполняется ли обновление
            // Это важно, так как между проверкой существования и созданием нового объекта
            // может пройти время, и другой эффект может начать обновление
            if (isUpdatingRef.current) {
                return
            }

            // Устанавливаем флаг обновления перед созданием нового объекта
            isUpdatingRef.current = true

            // Обновляем positionRef из пропсов
            if (position !== undefined) {
                positionRef.current = position ?? null
            }

            // Не создаем новый объект, если он создается в requestAnimationFrame
            if (isCreatingInRAFRef.current) {
                isUpdatingRef.current = false
                return
            }

            // Очищаем ref перед созданием
            textObjectRef.current = null

            // Создаем новый текстовый объект
            const textObj = await createTextObject({
                text,
                fontSize,
                textColor,
                textAlignment,
                fontFamily,
                hasBackground,
                backgroundColor,
                position,
                positionRef,
                fabricCanvas,
                safeArea,
                onFontSizeChange,
                onPositionChange,
                handleTextScaling,
                handleTextModified,
                isScalingFromSlider: () => isScalingFromSliderRef.current,
            })

            // Если создание не удалось (объект был удален другим эффектом)
            if (!textObj) {
                isUpdatingRef.current = false
                return
            }

            // Сохраняем ссылку на созданный объект
            textObjectRef.current = textObj
            isTextJustCreatedRef.current = true

            // Сбрасываем флаг обновления после успешного создания объекта
            isUpdatingRef.current = false
        }

        // Используем requestAnimationFrame для гарантии, что canvas отрендерен
        // Это предотвращает проблемы с позиционированием при первом открытии
        requestAnimationFrame(() => {
            // Дополнительная проверка готовности перед созданием текста
            if (
                !fabricCanvasRef.current ||
                !imageObjectRef.current ||
                !mediaDimensions
            ) {
                return
            }

            // Вызываем асинхронную функцию
            createOrUpdateTextObject().catch(() => {
                // Ошибка обрабатывается через состояние компонента
            })
                .finally(() => {
                    // Сбрасываем флаг обновления в любом случае
                    isUpdatingRef.current = false
                })
        })

        // Cleanup при размонтировании
        return () => {
            if (textObjectRef.current) {
                textObjectRef.current.off('scaling', handleTextScaling)
            }
            if (onPositionChange && fabricCanvasRef.current) {
                fabricCanvasRef.current.off(
                    'object:modified',
                    handleTextModified
                )
            }
        }
    }, [
        text,
        textColor,
        hasBackground,
        backgroundColor,
        textAlignment,
        fontFamily,
        position,
        fabricCanvasRef,
        textObjectRef,
        positionRef,
        previousAlignmentRef,
        isTextJustCreatedRef,
        isCreatingInRAFRef,
        isScalingFromSliderRef,
        safeArea,
        onFontSizeChange,
        onPositionChange,
        handleTextScaling,
        handleTextModified,
        fontSize,
        canvasReadyTrigger,
        isMediaLoading,
        imageObjectRef,
        mediaDimensions,
    ])

    // Обновление размера шрифта (синхронизация с ползунком)
    useTextObjectFontSizeUpdate({
        fontSize,
        fontFamily,
        hasBackground,
        backgroundColor,
        fabricCanvasRef,
        textObjectRef,
        isScalingFromSliderRef,
        handleTextScaling,
        safeArea,
        mediaDimensions,
    })
}
